import { useMutation } from "@tanstack/react-query";
import { AICoachReviewRequestPayload, generateAIReview } from "../api/ai-coach-reviews.api";
import { AICoachReviewsMode, AICoachReviewsTone, AICoachReviewsLength } from "common/src/gamePage/types";
import { getDraftKey, useCoachReviewsStore } from "../stores/coach-reviews.store";
import { useActiveCoachReviewsContext } from "../hooks/useActiveCoachReviewsContext";
import { useJwtAccessToken } from "common/src/gamePage/hooks/useJwtAccessToken";
import { AICoachReviewError } from "common/src/gamePage/errors/ai-coach.errors";

type AIReviewTarget =
	| { mode: AICoachReviewsMode.All; excludePlayerIds?: number[]; excludeTeamReview?: boolean }
	| { mode: AICoachReviewsMode.Team }
	| { mode: AICoachReviewsMode.Player; playerId: number };

type GenerateReviewsPayload = AIReviewTarget & {
	tone: AICoachReviewsTone;
	length: AICoachReviewsLength;
};

export const useAICoachReviews = () => {
	const { getToken } = useJwtAccessToken();
	const { userId, gameId, gameTeamId, teamId } = useActiveCoachReviewsContext();
	const upsertDraft = useCoachReviewsStore((state) => state.upsertDraft);
	const setIsLoading = useCoachReviewsStore((state) => state.setIsLoading);
	const { mutateAsync } = useMutation({ mutationFn: generateAIReview });

	const buildRequestPayload = (payload: GenerateReviewsPayload): AICoachReviewRequestPayload => {
		if (teamId === null) {
			throw new AICoachReviewError("Cannot generate AI coach review for a game team without a team");
		}

		if (payload.mode === AICoachReviewsMode.All) {
			return { ...payload, gameId, teamId };
		}

		const draftKey =
			payload.mode === AICoachReviewsMode.Team
				? getDraftKey({ kind: "team", userId, gameId, teamId: gameTeamId })
				: getDraftKey({ kind: "player", userId, gameId, playerId: payload.playerId });
		const draft = useCoachReviewsStore.getState().drafts[draftKey];

		return {
			...payload,
			gameId,
			teamId,
			reviewText: draft?.reviewText ?? undefined,
			attachedEventIds: draft?.attachedEventIds ?? undefined,
		};
	};

	const generateReviews = async (payload: GenerateReviewsPayload) => {
		const requestPayload = buildRequestPayload(payload);

		setIsLoading(true);
		try {
			const accessToken = await getToken();

			const { data: reviews } = await mutateAsync({
				payload: requestPayload,
				accessToken,
			});

			if (reviews.teamReview?.ok) {
				upsertDraft({
					kind: "team",
					userId: userId,
					gameId: reviews.gameId,
					teamId: gameTeamId,
					reviewText: reviews.teamReview.text,
					contentId: reviews.teamReview.contentId,
					generatedWithAI: true,
				});
			}

			reviews.playerReviews.forEach((playerReview) => {
				if (playerReview.ok === false) {
					return;
				}

				upsertDraft({
					kind: "player",
					userId: userId,
					gameId: reviews.gameId,
					playerId: playerReview.gamePlayerId,
					reviewText: playerReview.text,
					contentId: playerReview.contentId,
					generatedWithAI: true,
				});
			});
		} finally {
			setIsLoading(false);
		}
	};

	return {
		generateReviews,
	};
};
